import React from "react";
import PropTypes from "prop-types";
import { bgGrayLighter } from "../../constants/colors";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { PieChart, Pie, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import ScienceIcon from "@mui/icons-material/Science";
import { ReactComponent as BiosimilarsIcon } from "../../assets/Biosimilars.svg";
import { ReactComponent as CoPayAssistIcon } from "../../assets/CoPayAssist.svg";
import { ReactComponent as ECMPIcon } from "../../assets/ECMP.svg";
import { ReactComponent as GLP1Icon } from "../../assets/GLP 1.svg";
import { ReactComponent as IntSourcingIcon } from "../../assets/IntSourcing.svg";
import { ReactComponent as Womenshealth } from "../../assets/WomensHealth.svg";
import { ReactComponent as PAPIcon } from "../../assets/PAP.svg";
import LucyLogo from "../../assets/lucy.svg";
const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
    r / 255
  } 0 0 0 0 ${g / 255} 0 0 0 0 ${
    b / 255
  } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

const formatTitleCase = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const DashboardPDFExport = ({
  memberOverviewData,
  outcomes,
  mergedProgramData,
  dateRange,
  appliedFilters = { carriers: [], groups: [] },
  userRole = "",
  allAvailableCarriers = [],
}) => {
  const getProgramIcons = (
    programName,
    customStyle = null,
    iconColor = "#403C6F",
  ) => {
    const iconStyle = {
      width: "16px",
      height: "16px",
      marginRight: "4px",
      filter: getSvgFilter(iconColor),
      ...customStyle,
    };

    const icons = {
      Biosimilars: <BiosimilarsIcon style={iconStyle} />,
      "Copay Assist": <CoPayAssistIcon style={iconStyle} />,
      "Copay Assistance": <CoPayAssistIcon style={iconStyle} />,
      ECMP: <ECMPIcon style={iconStyle} />,
      "GLP-1": <GLP1Icon style={iconStyle} />,
      "International Sourcing": <IntSourcingIcon style={iconStyle} />,
      PAP: <PAPIcon style={iconStyle} />,
      "Women's Health Program": <Womenshealth style={iconStyle} />,
    };

    return (
      icons[programName] ||
      icons[
        Object.keys(icons).find(
          (key) => key.toLowerCase() === programName?.toLowerCase(),
        )
      ] || (
        <ScienceIcon
          style={{
            ...iconStyle,
            color: iconColor,
          }}
          htmlColor={iconColor}
        />
      )
    );
  };

  return (
    <Box
      sx={{
        width: "1000px", // Standard width for a nice PDF render
        p: 2,
        background: "#F7F8FB",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Header with Logo and Date */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        {/* Left: Halo Report Info */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#403C6F", mb: 0 }}
          >
            Halo Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Generated on {dayjs().format("MMMM D, YYYY h:mm A")}
          </Typography>
        </Box>

        {/* Center: Logo */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <img
            src={LucyLogo}
            alt="LucyRx"
            style={{ height: "40px", marginBottom: "8px" }}
          />
        </Box>

        {/* Right: Reporting Period */}
        <Box
          sx={{
            flex: 1,
            textAlign: "right",
            minWidth: "160px",
            alignSelf: "flex-start",
          }}
        >
          {(dateRange?.startDate || dateRange.endDate) && (
            <>
              <Typography
                sx={{ fontWeight: 600, color: "#403C6F", fontSize: "14px" }}
              >
                Reporting Period:
              </Typography>
              <Typography sx={{ color: "#1B2F3F", fontSize: "14px" }}>
                {dayjs(dateRange.startDate).format("MM/DD/YYYY")} -{" "}
                {dayjs(dateRange.endDate).format("MM/DD/YYYY")}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Carriers Filter Row */}
      {(appliedFilters?.carriers?.length > 0 ||
        (userRole === "L8" && allAvailableCarriers?.length > 0)) && (
        <Box sx={{ mt: -2, mb: 1 }}>
          <Typography
            sx={{ fontWeight: 600, color: "#403C6F", fontSize: "14px" }}
          >
            Carriers:
          </Typography>
          <Typography sx={{ color: "#1B2F3F", fontSize: "14px" }}>
            {(userRole === "L8" && (allAvailableCarriers || []).length > 0
              ? allAvailableCarriers
              : appliedFilters?.carriers || []
            )
              .map((c) => formatTitleCase(c.split(" (")[0]))
              .join(", ")}
          </Typography>
        </Box>
      )}

      {/* Overview Charts Row */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Member Overview Card */}
        <Card
          sx={{
            flex: 1.2,
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #E8E8E9",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #E8E8E9",
              background: "#FFF",
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
            >
              Opportunity Overview
            </Typography>
          </Box>
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              pt: 5,
            }}
          >
            <Box sx={{ position: "relative", width: "180px", height: "180px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={(memberOverviewData.total === 0 ||
                    memberOverviewData.total === "0" ||
                    memberOverviewData.total === "-"
                      ? [{ name: "Loading", value: 1, color: bgGrayLighter }]
                      : memberOverviewData.pieData
                    ).map((entry) => ({ ...entry, fill: entry.color }))}
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    paddingAngle={
                      memberOverviewData.total === 0 ||
                      memberOverviewData.total === "0" ||
                      memberOverviewData.total === "-"
                        ? 0
                        : 2.5
                    }
                    dataKey="value"
                    stroke="none"
                  />
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>
                  Total Opportunities
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "20px", color: "#403C6F" }}
                >
                  {memberOverviewData.total}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: 1,
                ml: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      bgcolor: "#45B184",
                    }}
                  />
                  <Typography sx={{ fontWeight: 500, color: "#1B2F3F" }}>
                    Complete
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#403C6F", ml: 4 }}>
                  {memberOverviewData.complete}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      bgcolor: "#E77877",
                    }}
                  />
                  <Typography sx={{ fontWeight: 500, color: "#1B2F3F" }}>
                    Incomplete
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#403C6F",
                    ml: 3,
                  }}
                >
                  {memberOverviewData.incomplete}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Outreach Outcome Summary Card */}
        <Card
          sx={{
            flex: 1.4,
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #E8E8E9",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #E8E8E9",
              background: "#FFF",
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
            >
              Outreach Outcome Summary
            </Typography>
          </Box>
          <CardContent sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              {outcomes.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1px solid #E0E0EE",
                    borderRadius: "6px",
                    p: "10px 12px",
                    bgcolor: "#FFF",
                    position: "relative",
                    pl: "18px",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: "6px",
                      height: "100%",
                      left: 0,
                      top: 0,
                      background: item.color,
                      borderRadius: "6px 0 0 6px",
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "12px", fontWeight: 500, color: "#1B2F3F" }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 700, color: "#403C6F" }}
                  >
                    {item.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Program Performance Table */}
      <Box
        sx={{
          mt: 1,
          background: "#FFF",
          borderRadius: "8px",
          border: "1px solid #E8E8E9",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#403C6F",
            mb: 2,
            borderBottom: "1px solid #E8E8E9",
            p: 2,
          }}
        >
          Program Performance Overview
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #E8E8E9",
            borderRadius: "8px",
            m: 2,
            width: "97%",
          }}
        >
          <Table
            size="small"
            sx={{
              "& .MuiTableBody-root .MuiTableCell-root": {
                borderBottom: "none",
                py: 1.5,
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#CFCEDA" }}>
                <TableCell sx={{ fontWeight: 600, color: "#403C6F", py: 1.5 }}>
                  Program Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 600, color: "#403C6F" }}
                >
                  Total Opportunities
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 600, color: "#403C6F" }}
                >
                  Enrolled Opportunities
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mergedProgramData?.length > 0 ? (
                mergedProgramData.map((row) => (
                  <TableRow
                    key={row.name}
                    sx={{ "&:last-child td": { border: 0 } }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: "12px",
                          bgcolor: "#ECECF1",
                          width: "fit-content",
                        }}
                      >
                        {getProgramIcons(row.name)}
                        <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                          {row.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{row.eligible}</TableCell>
                    <TableCell align="center">{row.enrolled}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ py: 3, color: "#6B7280" }}
                  >
                    No Records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Footer / Disclaimer */}
      <Box sx={{ mt: "auto", pt: 4, pb: 2, textAlign: "center" }}>
        <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
          This report contains confidential information. &copy;{" "}
          {new Date().getFullYear()} LucyRx. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

DashboardPDFExport.propTypes = {
  memberOverviewData: PropTypes.object.isRequired,
  outcomes: PropTypes.array.isRequired,
  mergedProgramData: PropTypes.array.isRequired,
  dateRange: PropTypes.object.isRequired,
  appliedFilters: PropTypes.object,
  userRole: PropTypes.string,
  allAvailableCarriers: PropTypes.array,
};

export default DashboardPDFExport;
